import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useBetaCredits } from '../hooks/useBetaCredits';
import { useMockDetectorControl } from '../hooks/useMockDetectorControl';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { WIDGET_VERSION, COPYRIGHT_TEXT } from '../utils/version';
import { Coins, MessageSquare, EyeOff, Trash2, Lock, Check, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';

interface MockDetectorSettingsProps {
  onClose?: () => void;
  onHide?: () => void;
  onDisable?: () => void;
}

export const MockDetectorSettings: React.FC<MockDetectorSettingsProps> = ({ 
  onClose, 
  onHide, 
  onDisable
}) => {
  const {
    credits,
    remainingDailyScans,
    maxDailyScans,
    feedbackSubmitted,
    canSubmitFeedback,
    submitFeedback,
    feedbackBonus,
    initialCredits = 50, // Fallback if not returned
  } = useBetaCredits();

  const {
    disablePermanently,
    getUninstallInstructions,
  } = useMockDetectorControl();

  const { 
    currentVersion, 
    latestVersion
  } = useVersionCheck();

  const [feedbackText, setFeedbackText] = useState('');
  const maxFeedbackLength = 500;

  const handleSendFeedback = async () => {
    if (!feedbackText.trim() || feedbackText.trim().length < 10) {
      toast({
        title: "Validation Error",
        description: "Please write at least 10 characters of feedback",
        variant: "destructive"
      });
      return;
    }

    if (!canSubmitFeedback()) {
      toast({
        title: "Already Submitted",
        description: "Feedback already submitted or API key missing",
        variant: "destructive"
      });
      return;
    }

    const result = await submitFeedback(feedbackText);
    
    if (result.success) {
      toast({
        title: `🎉 +${feedbackBonus} Credits Added!`,
        description: "Thank you for your feedback!"
      });
      setFeedbackText('');
    } else {
      toast({
        title: "Error",
        description: result.error || 'Failed to submit feedback. Please try again.',
        variant: "destructive"
      });
    }
  };

  const uninstallInstructions = getUninstallInstructions();

  return (
    <div className="space-y-4">
      {/* Beta Status Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-yellow-500" />
          <h3 className="font-semibold text-sm">Credit Balance</h3>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-3 space-y-2 relative">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Credits Remaining</span>
            <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
              {credits} credits
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Daily Scans</span>
            <Badge>
              {remainingDailyScans} / {maxDailyScans} left
            </Badge>
          </div>
          
          <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/50">
            {credits === 'Unlimited (Dev)' 
              ? '💡 Development mode - unlimited scans within daily limit'
              : `💡 New users start with ${initialCredits} credits. Earn +${feedbackBonus} by submitting feedback below!`
            }
          </p>
        </div>
      </div>

      {/* Storage Info */}
      <div className="space-y-3">
        <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-2.5">
          <p className="text-xs text-white dark:text-blue-50 leading-relaxed">
            💾 <strong>Simplified Storage</strong>
          </p>
          <ul className="text-[10px] text-blue-100/90 mt-1.5 space-y-0.5 ml-2">
            <li>✅ Latest scan stored in browser (persists after F5)</li>
            <li>⚠️ Scans 5MB+: Auto-saves top 4000 errors</li>
            <li>📥 Full report always downloadable (kept in memory)</li>
            <li>🔄 Each new scan replaces the old one</li>
          </ul>
        </div>
      </div>

      {/* Feedback Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-blue-500" />
          <h3 className="font-semibold text-sm">Share Feedback</h3>
        </div>
        
        {feedbackSubmitted ? (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 flex items-center gap-2">
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-xs text-green-600 dark:text-green-400">
              Thank you for your feedback! +{feedbackBonus} bonus credits awarded.
            </span>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Share your feedback and get +{feedbackBonus} bonus credits! (One-time offer)
            </p>
            
            <div className="relative">
              <Textarea
                placeholder="What do you think about MockDetector? Any bugs, suggestions, or features you'd like to see?"
                value={feedbackText}
                onChange={(e) => {
                  const text = e.target.value;
                  if (text.length <= maxFeedbackLength) {
                    setFeedbackText(text);
                  }
                }}
                className="min-h-[100px] text-xs resize-none pb-10"
                maxLength={maxFeedbackLength}
              />
              <div className="absolute bottom-2 right-2 text-xs text-muted-foreground pointer-events-none">
                {feedbackText.length}/{maxFeedbackLength}
              </div>
            </div>
            
            <Button
              onClick={handleSendFeedback}
              disabled={!feedbackText.trim() || !canSubmitFeedback()}
              className="w-full"
              size="sm"
            >
              <MessageSquare className="h-3 w-3 mr-2" />
              Send Feedback & Get +{feedbackBonus} Credits
            </Button>
          </div>
        )}
      </div>

      {/* Version Info Section (Read-only) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-blue-500" />
          <h3 className="font-semibold text-sm">Version Info</h3>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Current Version</span>
            <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 font-mono">
              v{currentVersion}
            </Badge>
          </div>
          
          {latestVersion && latestVersion !== currentVersion && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Latest Version</span>
              <Badge className="bg-green-500/10 text-green-500 border-green-500/20 font-mono">
                v{latestVersion}
              </Badge>
            </div>
          )}
          
          <p className="text-[10px] text-muted-foreground pt-2 border-t border-border/50">
            ✨ Updates happen automatically in the background! No action needed.
          </p>
        </div>
      </div>

      {/* Widget Control */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <EyeOff className="h-4 w-4 text-orange-500" />
          <h3 className="font-semibold text-sm">Widget Control</h3>
        </div>
        
        <div className="space-y-2">
          <Button
            onClick={() => {
              console.log('🔴 HIDE BUTTON CLICKED');
              toast({
                title: "Hidden Until Refresh",
                description: "MockDetector will reappear after F5"
              });
              onHide?.();
            }}
            variant="outline"
            size="sm"
            className="w-full justify-start text-xs"
          >
            <EyeOff className="h-3 w-3 mr-2" />
            Hide Until Refresh (F5)
          </Button>
          
          <Button
            onClick={() => {
              if (confirm('Are you sure? MockDetector will be hidden until you click "Re-enable".')) {
                disablePermanently();
                toast({
                  title: "MockDetector Disabled",
                  description: "Click the green card to re-enable"
                });
                onDisable?.();
              }
            }}
            variant="outline"
            size="sm"
            className="w-full justify-start text-xs text-orange-600 hover:text-orange-700"
          >
            <Lock className="h-3 w-3 mr-2" />
            Disable Permanently
          </Button>
        </div>
      </div>

      {/* Uninstall Instructions */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-red-500" />
          <h3 className="font-semibold text-sm">Uninstall</h3>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <p className="text-xs text-muted-foreground mb-2">To completely remove MockDetector:</p>
          
          <div className="space-y-1.5">
            <div className="bg-background rounded px-2 py-1">
              <code className="text-xs text-red-600 dark:text-red-400">
                {uninstallInstructions.npm}
              </code>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {uninstallInstructions.removeImport}
            </p>
            
            <p className="text-xs text-muted-foreground italic">
              {uninstallInstructions.alternative}
            </p>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Built by <span className="font-semibold text-foreground">Silver</span> (Anton Fredriksson) 🇸🇪
          </p>
          <p className="text-xs text-muted-foreground">
            with AI assistance 🤖
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Version: {WIDGET_VERSION}
          </p>
          <p className="text-[10px] text-muted-foreground pt-2 border-t border-border/50">
            {COPYRIGHT_TEXT}
          </p>
      </div>

      {/* Close Button */}
      <Button 
        onClick={onClose} 
        variant="outline" 
        className="w-full"
        size="sm"
      >
        Close
      </Button>
    </div>
  );
};
